import Cookies from 'js-cookie'
import * as React from 'react'
import type {Cart, Product, CartContextInterface} from './types'
import * as LocalStorageWithExpiry from './utils'

const CART_KEY = 'TBT_CART'
const EMPTY_CART = {items: [], orderId: 0, eventId: 0, isPurchase: false}

const get24HoursFromNow = () => new Date(new Date().getTime() + 60 * 60 * 24 * 1000)

// Context
export const CartContext = React.createContext<CartContextInterface>({
  activateCart: () => {
    return null
  },
  deactivateCart: () => {
    return null
  },
  addToCart: () => null,
  removeFromCart: () => null,
  isActive: false,
  cart: EMPTY_CART,
})

// Provider
export const CartContextProvider: React.FC = ({children}) => {
  const [cart, setCart] = React.useState(() => {
    try {
      const cookieCart = LocalStorageWithExpiry.getItemWithExpiry(CART_KEY)
      return cookieCart ? JSON.parse(cookieCart) : null
    } catch (err) {
      console.error(err)
      return null
    }
  })

  const isActive = Boolean(cart) && cart.orderId && cart.eventId

  const activateCart = React.useCallback(
    (eventId: number, orderId: number, isPurchase: boolean) => {
      if (isActive) return

      const newCart = {
        ...EMPTY_CART,
        eventId,
        orderId,
        isPurchase,
      }

      setCart(newCart)
      LocalStorageWithExpiry.setItemWithExpiry(
        CART_KEY,
        JSON.stringify(newCart),
        get24HoursFromNow(),
      )
      Cookies.set(CART_KEY, 'true', {expires: get24HoursFromNow()})
    },
    [isActive],
  )

  const deactivateCart = React.useCallback(() => {
    setCart(null)
    localStorage.removeItem(CART_KEY)
    localStorage.removeItem(`${CART_KEY}-expiry`)
    Cookies.remove(CART_KEY)
  }, [])

  const addToCart = (item: Product) => {
    if (!item) return

    setCart((prevCart: Cart) => {
      const newCart = {
        ...cart,
        items: [
          ...prevCart.items,
          {
            ...item,
            tempId: `${item.slug}-${new Date().valueOf()}`, // client-side-only to differentiate duplicate items
          },
        ],
      }

      LocalStorageWithExpiry.setItemWithExpiry(
        CART_KEY,
        JSON.stringify(newCart),
        get24HoursFromNow(),
      )
      Cookies.set(CART_KEY, 'true', {expires: get24HoursFromNow()})
      return newCart
    })
  }

  const removeFromCart = (itemTempId: string) => {
    setCart((prevCart: Cart) => {
      const newCart = {
        ...cart,
        items: prevCart.items.filter((i) => i.tempId !== itemTempId),
      }

      LocalStorageWithExpiry.setItemWithExpiry(
        CART_KEY,
        JSON.stringify(newCart),
        get24HoursFromNow(),
      )
      Cookies.set(CART_KEY, 'true', {expires: get24HoursFromNow()})
      return newCart
    })
  }

  return (
    <CartContext.Provider
      value={{isActive, cart, activateCart, deactivateCart, addToCart, removeFromCart}}
    >
      {children}
    </CartContext.Provider>
  )
}
